/* @flow */
import * as React from 'react';
import { debounce } from 'lodash';
import { WithRouterProps } from 'react-router';

import { Query } from 'react-apollo';
import GROUP_CREATE_MODAL_QUERY from 'groups/queries/GroupCreateModalQuery.gql';

import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import ConfirmDialog from 'components/ConfirmDialog';
import CurrentUser from 'auth/CurrentUser';
import Dialog from 'components/Dialog';
import Divider from 'components/Divider';
import enumerate from 'lib/enumerate';
import errorLogger from 'utils/errorLogger';
import ErrorReporter from 'app/components/ErrorReporter';
import Group from 'entities/Group';
import GroupActions from 'groups/actions/GroupActions';
import GroupAvatarSelector from './GroupAvatarSelector';
import GroupForm from './GroupForm';
import GroupOptionsForm from './GroupOptionsForm';
import GroupRequestApprovalModal from './GroupRequestApprovalModal';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import SETTINGS_ROUTES from 'settings/constants/SettingsRouteConstants';
import ToastActions from 'actions/ToastActions';
// @ts-ignore
import { UNAFFILIATED_CLASS_ID } from 'groups/components/GroupSchoolSelect';
import { withRouter } from 'app/context/Router';

const DIALOG_STATUS = enumerate(['CREATE_CLASS', 'REQUEST_CLASS_APPROVAL']);

export type Props = {
  onCreate?: ?(group: Group) => void,
  onRequestHide: () => void,
} & WithRouterProps;

type State = {
  hasChildren: boolean,
  code: ?string,
  name: ?string,
  avatarHandpicked: boolean,
  groupCodeHandpicked: boolean,
  suggestedGroupCode: boolean,
  canSubmit: boolean,
  dialogShown: typeof DIALOG_STATUS,
  classToRequest: Group,
  selectedAvatarUrl: string,
  selectedAvatarId: number | null,
  classRequestButtonEnabled: boolean,
  selectedOrgUuid: string,
  selectedOrgId: string | null,
  selectedOrgName: string,
};

class GroupCreateModal extends React.Component<Props, State> {
  state: State = {
    hasChildren: false,
    code: null,
    name: null,
    selectedOrgId: '',
    selectedOrgName: '',
    selectedOrgUuid: '',
    avatarHandpicked: false,
    groupCodeHandpicked: false,
    suggestedGroupCode: false,
    canSubmit: false,
    dialogShown: DIALOG_STATUS.CREATE_CLASS,
    classToRequest: new Group(),
    selectedAvatarUrl: '',
    selectedAvatarId: null,
    classRequestButtonEnabled: false,
  };

  avatarSelector: ?React.Element<typeof GroupAvatarSelector> = React.createRef();

  cancelRequestApproval = () => {
    this.setState({ dialogShown: DIALOG_STATUS.CREATE_CLASS });
  };

  handleApprovalReasonInput = requestReasonText => {
    this.setState({
      classRequestButtonEnabled: requestReasonText.length >= 3,
    });
  };

  suggestGroupCode = debounce((name: string) => {
    Group.suggestCode(name)
      .then(codes => {
        if (!this.state.code && codes && codes[0]) {
          this.setState({ code: codes[0] });
          this.refs.groupForm.setState({ codeFromField: codes[0] });
        }
        this.setState({ suggestedGroupCode: true });
      })
      .catch(errorLogger('GroupCreateModal suggestCode failed'));
  }, 300);

  suggestAvatarForName = debounce((name: string) => {
    if (!this || !this.avatarSelector || !this.avatarSelector.current) {
      return;
    }
    this.avatarSelector.current.searchAvatarForName(name);
  }, 300);

  handleAvatarSelected = () => {
    this.setState({ avatarHandpicked: true });
  };

  handleCodeChange = () => {
    if (!this.state.groupCodeHandpicked) {
      this.setState({ groupCodeHandpicked: true });
    }
  };

  handleOptionsChange = (name: string, value: any) => {
    this.setState({ [name]: value });
  };

  handleSubmit = (needRequestApproval = false) => {
    if (!this || !this.avatarSelector || !this.avatarSelector.current) {
      return;
    }

    const { selectedOrgId } = this.state;
    const orgData = selectedOrgId
      ? {
          organization: {
            id: selectedOrgId === UNAFFILIATED_CLASS_ID ? null : selectedOrgId || undefined,
          },
        }
      : {};

    const groupToCreate = new Group({
      name: this.state.code,
      class_name: this.state.name,
      group_avatar: {
        id: this.avatarSelector.current.getSelectedAvatarId(),
      },
      ...this.refs.options.getValues(),
      ...orgData,
    });

    if (needRequestApproval) {
      const selectedAvatar = this.avatarSelector && this.avatarSelector.current.getSelectedAvatar();

      this.setState({
        dialogShown: DIALOG_STATUS.REQUEST_CLASS_APPROVAL,
        classToRequest: groupToCreate,
        selectedAvatarId: selectedAvatar?.attributes.id,
        selectedAvatarUrl: selectedAvatar?.attributes.svg_url,
      });
    } else {
      GroupActions.create(groupToCreate)
        .then(group => {
          this.props.onRequestHide();
          if (this.props.onCreate) {
            this.props.onCreate(group);
          }
        })
        .catch(ToastActions.showAPIError);
    }
  };

  handleFormInvalid = () => {
    // Prevent excessive re-renders
    if (this.state.canSubmit) {
      this.setState({ canSubmit: false });
    }
  };

  handleFormValid = (
    name: string,
    code: string,
    orgId?: ?string,
    orgName?: string,
    orgUuid?: string
  ) => {
    this.setState({
      name,
      code,
      // TODO -- add a test for this (to avoid future p0s)
      // if the orgId is equal to the UNAFFILIATED_CLASS_ID, then we need to set this key to null,
      // otherwise pass in the org id
      // if the orgId is undefined for some god forsaken reason then use that in the key so that it doesn't get sent to r101-api as null and
      //   remove us from the org
      selectedOrgId: orgId,
      selectedOrgUuid: orgUuid,
      selectedOrgName: orgName || this.state.selectedOrgName,
      canSubmit: true,
    });
  };

  handleSchoolChange = (orgId, orgName, orgUuid) => {
    this.setState({
      selectedOrgId: orgId,
      selectedOrgUuid: orgUuid,
      selectedOrgName: orgName,
    });
  };

  handleValidNameChange = (name: string) => {
    if (!this.state.groupCodeHandpicked && !this.state.suggestedGroupCode) {
      this.suggestGroupCode(name);
    }

    if (!this.state.avatarHandpicked) {
      this.suggestAvatarForName(name);
    }
  };

  renderSubmitButton(selectedOrgUuid) {
    const submitButtonElement = props => (
      <Button
        className="GroupCreateModal-submitButton"
        data-test-selector="group-create-modal-submit"
        {...props}>
        Create
      </Button>
    );

    if (selectedOrgUuid === undefined) {
      return submitButtonElement({
        disabled: !this.state.canSubmit,
        onClick: () => this.handleSubmit(false),
      });
    } else {
      return (
        <Query
          query={GROUP_CREATE_MODAL_QUERY}
          variables={{ orgUuid: selectedOrgUuid }}
          fetchPolicy="network-only">
          {({ loading, error, data }) => {
            const gateClasses = data?.organizations?.[0]?.approvalSettings?.gateClasses;
            return submitButtonElement({
              disabled: !this.state.canSubmit || loading || error,
              onClick: () => this.handleSubmit(gateClasses),
            });
          }}
        </Query>
      );
    }
  }

  handleSubmitRequestClassApproval = () => {
    // TODO: Add Request Class Approval mutation
    const { onRequestHide } = this.props;
    onRequestHide();
  };

  render() {
    if (!CurrentUser.isSignedIn()) {
      return <ErrorReporter error="GroupCreateModal - user is not signed in" />;
    }

    const user = CurrentUser.getUser();

    if (user && !user.hasEmail()) {
      return (
        <ConfirmDialog
          title="Add an email address to your account first"
          subtext="An email address is required to create a class. You can add one in your account settings."
          actionButtonText="Go to Settings"
          cancelButtonText="Cancel"
          onRequestHide={this.props.onRequestHide}
          onConfirm={() => this.props.router.push(SETTINGS_ROUTES.NOTIFICATIONS)}
        />
      );
    }

    const { onRequestHide } = this.props;
    const {
      code,
      classToRequest,
      classRequestButtonEnabled,
      dialogShown,
      hasChildren,
      name,
      selectedAvatarId,
      selectedAvatarUrl,
      selectedOrgId,
      selectedOrgName,
      selectedOrgUuid,
    } = this.state;

    if (dialogShown === DIALOG_STATUS.REQUEST_CLASS_APPROVAL) {
      return (
        <GroupRequestApprovalModal
          classRequestButtonEnabled={classRequestButtonEnabled}
          classToRequest={classToRequest}
          onChange={this.handleApprovalReasonInput}
          onClickCancel={this.cancelRequestApproval}
          onRequestHide={onRequestHide}
          onSubmit={this.handleSubmitRequestClassApproval}
          orgName={selectedOrgName}
          selectedAvatarUrl={selectedAvatarUrl}
        />
      );
    }

    const groupFormProps = selectedOrgName
      ? {
          currentOrganization: {
            value: selectedOrgId || '',
            label: selectedOrgName,
          },
        }
      : {};

    return (
      <Dialog
        {...this.props}
        eventScreenName="GroupCreateModal"
        className="GroupCreateModal"
        title="Create a class"
        size="small">
        <BoxBody divided>
          <MediaObject className="GroupCreateModal-top">
            <div>
              <GroupAvatarSelector
                ref={this.avatarSelector}
                initialAvatar={selectedAvatarId ? { id: selectedAvatarId } : null}
                onSelect={this.handleAvatarSelected}
              />
            </div>
            <div>
              <GroupForm
                ref="groupForm"
                name={name || ''}
                code={code || ''}
                onFormValid={this.handleFormValid}
                onValidNameChange={this.handleValidNameChange}
                onCodeChange={this.handleCodeChange}
                onFormInvalid={this.handleFormInvalid}
                hasChildren={hasChildren}
                showOrg
                onSchoolChange={this.handleSchoolChange}
                {...groupFormProps}
              />
            </div>
          </MediaObject>
          <Divider size="medium" />
          <Row>
            <GroupOptionsForm
              ref="options"
              subscriberInitiatedChats
              hasChildren={hasChildren}
              showSearchable={false}
              userIsTeacher={CurrentUser.getUser().isTeacher()}
              onChange={this.handleOptionsChange}
              showRequestToJoin={false}
            />
          </Row>
          {this.renderSubmitButton(selectedOrgUuid)}
        </BoxBody>
      </Dialog>
    );
  }
}

export default withRouter(GroupCreateModal);
